// src/lib/services/videoService.ts

import { db } from '../firebase';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  setDoc,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { logger } from '../logger';
import { handleAPIError } from '../errorHandling';
import { getAllChannelVideos, getUploadsPlaylistId, getVideoMetadata, getChannelAvatars } from '../youtube';
import { getUserProfile } from '../../profile/services/profileService';

export type VideoSource = 'native' | 'youtube';

export interface VideoStreams {
  hlsManifestUrl?: string;
  hlsProxyManifestUrl?: string;
  manifestUrl?: string;
  rawMp4Url?: string;
  downloadUrl?: string;
}

export interface Video {
  id: string;
  title: string;
  thumbnail: string;
  duration: number;
  type: 'short' | 'video' | 'live';
  createdAt?: unknown;
  publishedAt?: unknown;
  creatorId?: string;
  lastSynced?: string;
  description?: string;
  status?: 'uploading' | 'processing' | 'ready' | 'failed';
  rawUrl?: string;
  /** Content source: 'native' for ViewsBoost uploads, 'youtube' for YouTube content */
  source?: VideoSource;
  /** Optional YouTube ID override when Firestore doc ID differs from YouTube ID */
  youtubeId?: string;
  /** Optional aspect ratio metadata for native uploads */
  aspectRatio?: string;
  /** Optional dimensions for native uploads */
  width?: number;
  height?: number;
  /** For native videos: streaming + archive URLs */
  streams?: VideoStreams;
  /** Direct MP4 URL for published Studio videos */
  downloadUrl?: string;
  /** Channel/creator display name */
  channelName?: string;
/** Creator avatar URL */
  creatorAvatar?: string;
  /** Time ago label (e.g. "3d", "1h") */
  timeAgo?: string;
  /** Real YouTube view count (YouTube-sourced videos only) */
  viewCount?: number;
}

export interface VideoFeedPage {
  videos: Video[];
  nextCursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

const YOUTUBE_VIDEO_ID_RE = /^[a-zA-Z0-9_-]{11}$/;

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

export function looksLikeYouTubeVideoId(id?: string): boolean {
  return !!id && YOUTUBE_VIDEO_ID_RE.test(id);
}

export function getNativePreviewUrl(video: Partial<Video> & Record<string, any>): string | undefined {
  const streams = (video?.streams as VideoStreams | undefined) || {};
  return (
    readString(streams.rawMp4Url) ||
    readString(streams.downloadUrl) ||
    readString(video.downloadUrl) ||
    readString(video.rawUrl)
  );
}

export function hasNativePlaybackSources(video: Partial<Video> & Record<string, any>): boolean {
  const streams = (video?.streams as VideoStreams | undefined) || {};
  const playbackManifest = readString(video?.playback?.manifestUrl);
  return !!(
    getNativePreviewUrl(video) ||
    readString(streams.hlsManifestUrl) ||
    readString(streams.hlsProxyManifestUrl) ||
    readString(streams.manifestUrl) ||
    playbackManifest
  );
}

export function getYouTubeVideoId(video: Partial<Video> & Record<string, any>): string | undefined {
  const explicitId = readString(video.youtubeId);
  if (explicitId && looksLikeYouTubeVideoId(explicitId)) return explicitId;

  const id = readString(video.id);
  if (id && looksLikeYouTubeVideoId(id)) return id;
  return undefined;
}

export function inferVideoSource(video: Partial<Video> & Record<string, any>): VideoSource {
  if (video.source === 'native' || video.source === 'youtube') return video.source;
  if (hasNativePlaybackSources(video)) return 'native';
  if (getYouTubeVideoId(video)) return 'youtube';
  return 'native';
}

export function normalizeVideoDocument(id: string, data: Record<string, any>): Video {
  const streamsFromDoc = (data?.streams || {}) as VideoStreams;
  const normalizedStreams: VideoStreams = {
    hlsManifestUrl: readString(streamsFromDoc.hlsManifestUrl),
    hlsProxyManifestUrl: readString(streamsFromDoc.hlsProxyManifestUrl),
    manifestUrl: readString(streamsFromDoc.manifestUrl) || readString(data?.playback?.manifestUrl),
    rawMp4Url: readString(streamsFromDoc.rawMp4Url) || readString(data?.rawUrl),
    downloadUrl: readString(streamsFromDoc.downloadUrl) || readString(data?.downloadUrl),
  };

  const source = inferVideoSource({
    ...data,
    id,
    streams: normalizedStreams,
  });
  const youtubeId = getYouTubeVideoId({
    ...data,
    id,
  });
  const thumbnail =
    readString(data?.thumbnail) ||
    readString(data?.thumbnailData?.primary) ||
    (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : '');

  const rawDuration = Number(data?.duration);
  const normalizedDuration = Number.isFinite(rawDuration) ? rawDuration : 0;
  const normalizedType: Video['type'] =
    data?.type === 'short' || data?.type === 'video' || data?.type === 'live'
      ? data.type
      : 'video';

  // Normalize viewCount: prefer stored viewCount, fall back to native `views` counter
  const rawViewCount = Number(data?.viewCount);
  const rawViews = Number(data?.views);
  const normalizedViewCount = Number.isFinite(rawViewCount) && rawViewCount > 0
    ? rawViewCount
    : Number.isFinite(rawViews) && rawViews > 0
    ? rawViews
    : undefined;

  // Compute timeAgo eagerly so all code paths (sidebar, grid, etc.) have it
  const normalizedTimeAgo =
    readString(data?.timeAgo) ||
    computeTimeAgo(data?.publishedAt || data?.createdAt || data?.lastSynced) ||
    undefined;

  return {
    ...(data as Video),
    id,
    title: readString(data?.title) || 'Untitled',
    thumbnail,
    duration: normalizedDuration,
    type: normalizedType,
    source,
    youtubeId,
    rawUrl: readString(data?.rawUrl),
    downloadUrl: readString(data?.downloadUrl),
    streams: normalizedStreams,
    viewCount: normalizedViewCount,
    timeAgo: normalizedTimeAgo,
  };
}

function toMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof (value as any)?.toMillis === 'function') {
    const parsed = Number((value as any).toMillis());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof (value as any)?.toDate === 'function') {
    const parsed = (value as any).toDate();
    return parsed instanceof Date ? parsed.getTime() : 0;
  }
  if (typeof (value as any)?.seconds === 'number') {
    return Number((value as any).seconds) * 1000;
  }
  return 0;
}

function getFeedSortTimestamp(video: Partial<Video> & Record<string, any>): number {
  return (
    toMillis(video.publishedAt) ||
    toMillis(video.createdAt) ||
    toMillis(video.lastSynced) ||
    toMillis(video.updatedAt) ||
    0
  );
}

function shouldIncludeDashboardVideo(video: Video, includeNonReady: boolean): boolean {
  if (includeNonReady) return true;
  const source = inferVideoSource(video as Partial<Video> & Record<string, any>);
  if (source === 'youtube') return true;
  // Include processing/uploading so optimistic docs appear on the feed immediately
  return !video.status || video.status === 'ready' || video.status === 'processing' || video.status === 'uploading';
}

// Cache creator profiles to avoid redundant Firestore reads within a session
const creatorProfileCache = new Map<string, { displayName: string; photoURL: string }>();

/** Enrich an array of videos with creator avatar & channelName from their profiles */
export async function enrichVideosWithCreatorInfo(videos: Video[]): Promise<Video[]> {
  // Collect unique creatorIds that need fetching
  const idsToFetch = new Set<string>();
  for (const v of videos) {
    if (v.creatorId && !creatorProfileCache.has(v.creatorId)) {
      idsToFetch.add(v.creatorId);
    }
  }

  // Batch-fetch all missing profiles in parallel
  if (idsToFetch.size > 0) {
    const entries = await Promise.all(
      Array.from(idsToFetch).map(async (uid) => {
        const profile = await getUserProfile(uid);
        return [uid, profile] as const;
      })
    );
    for (const [uid, profile] of entries) {
      if (profile) {
        creatorProfileCache.set(uid, {
          displayName: profile.displayName,
          photoURL: profile.photoURL,
        });
      }
    }
  }

  // Map creator data onto videos — only native videos get ViewsBoost creator info.
  // YouTube videos keep their real YouTube channel name (not the VB uploader's name).
  return videos.map((v) => {
    if (!v.creatorId) return v;
    if (v.source === 'youtube') return v;
    const cached = creatorProfileCache.get(v.creatorId);
    if (!cached) return v;
    return {
      ...v,
      creatorAvatar: v.creatorAvatar || cached.photoURL,
      channelName: v.channelName || cached.displayName,
    };
  });
}

// ── YouTube metadata enrichment (backfills Firestore on first load) ───────

/** Session-level cache to avoid re-fetching YouTube metadata within one session */
const youtubeMetaCache = new Map<string, boolean>();

/** Compute short "time ago" label: 1h, 2d, 2w, 2m, 1y */
function computeTimeAgo(dateInput: unknown): string {
  const ms = toMillis(dateInput);
  if (!ms) return '';
  const seconds = Math.floor((Date.now() - ms) / 1000);
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))}h`;
  const hours = Math.floor(seconds / 3600);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}m`;
  const years = Math.floor(days / 365);
  return `${years}y`;
}

/**
 * Fetches real YouTube metadata (channelName, viewCount, publishedAt) for
 * YouTube videos that are missing it. Writes results back to Firestore so
 * subsequent loads don't need the API. Also computes `timeAgo` for all videos.
 */
export async function enrichYouTubeVideoMetadata(videos: Video[]): Promise<Video[]> {
  // Find YouTube videos missing real metadata (channelName, viewCount, or avatar)
  const needsFetch = videos.filter(v =>
    v.source === 'youtube' &&
    !youtubeMetaCache.has(v.id) &&
    (!v.channelName || !v.viewCount || !v.creatorAvatar)
  );

  // Fetch from YouTube API if needed
  const metaMap = new Map<string, {
    channelName: string;
    viewCount: number;
    publishedAt: string;
    creatorAvatar: string;
  }>();

  if (needsFetch.length > 0) {
    try {
      const ids = needsFetch.map(v => v.youtubeId || v.id);
      const metadata = await getVideoMetadata(ids);

      // Collect unique channel IDs to fetch avatars
      const channelIdToVideoIds = new Map<string, string[]>();
      for (const v of needsFetch) {
        const ytId = v.youtubeId || v.id;
        const meta = metadata[ytId];
        if (meta?.channelId) {
          const existing = channelIdToVideoIds.get(meta.channelId) || [];
          existing.push(v.id);
          channelIdToVideoIds.set(meta.channelId, existing);
        }
      }

      // Fetch channel avatars in one batched call
      const channelIds = [...channelIdToVideoIds.keys()];
      const avatars = channelIds.length > 0 ? await getChannelAvatars(channelIds) : {};

      for (const v of needsFetch) {
        const ytId = v.youtubeId || v.id;
        const meta = metadata[ytId];
        if (meta) {
          const avatar = avatars[meta.channelId] || '';
          metaMap.set(v.id, {
            channelName: meta.channelTitle,
            viewCount: meta.viewCount,
            publishedAt: meta.publishedAt,
            creatorAvatar: avatar,
          });
          youtubeMetaCache.set(v.id, true);

          // Backfill Firestore (fire-and-forget — non-blocking)
          setDoc(doc(db, 'videos', v.id), {
            channelName: meta.channelTitle,
            viewCount: meta.viewCount,
            publishedAt: meta.publishedAt,
            creatorAvatar: avatar,
          }, { merge: true }).catch(() => {});
        }
      }
    } catch (err) {
      logger.error('YouTube metadata enrichment failed:', err);
    }
  }

  // Apply fetched metadata + compute timeAgo for ALL videos
  return videos.map(v => {
    const fetched = metaMap.get(v.id);
    const merged = fetched ? { ...v, ...fetched } : v;

    // Compute timeAgo from publishedAt, createdAt, or lastSynced
    if (!merged.timeAgo) {
      const ago = computeTimeAgo(merged.publishedAt || merged.createdAt || merged.lastSynced);
      if (ago) merged.timeAgo = ago;
    }

    youtubeMetaCache.set(v.id, true);
    return merged;
  });
}

// Fetch all videos from Firestore (for UI/dashboard/feed)
export async function getVideos(): Promise<Video[]> {
  try {
    const snap = await getDocs(collection(db, 'videos'));
    const videos = snap.docs.map((d) => normalizeVideoDocument(d.id, d.data() as Record<string, any>));
    return enrichVideosWithCreatorInfo(videos);
  } catch (err) {
    logger.error('Error fetching videos:', err);
    return [];
  }
}

export async function listDashboardVideosPage(options?: {
  pageSize?: number;
  cursor?: QueryDocumentSnapshot<DocumentData> | null;
  includeNonReady?: boolean;
}): Promise<VideoFeedPage> {
  const pageSize = Math.max(1, Math.min(options?.pageSize ?? 24, 240));
  const includeNonReady = options?.includeNonReady === true;
  const candidateLimit = Math.max(pageSize * 3, pageSize + 32);

  try {
    const createdAtQuery = options?.cursor
      ? query(
          collection(db, 'videos'),
          orderBy('createdAt', 'desc'),
          startAfter(options.cursor),
          limit(candidateLimit + 1)
        )
      : query(
          collection(db, 'videos'),
          orderBy('createdAt', 'desc'),
          limit(candidateLimit + 1)
        );

    // Pull YouTube docs separately on the first page so entries without native-only fields
    // are not excluded by createdAt ordering.
    const queryTasks: Array<Promise<any>> = [getDocs(createdAtQuery)];
    if (!options?.cursor) {
      const youtubeQuery = query(
        collection(db, 'videos'),
        where('source', '==', 'youtube'),
        limit(candidateLimit)
      );
      queryTasks.push(getDocs(youtubeQuery));

      // Legacy YouTube docs may have no createdAt/source but do have lastSynced.
      const lastSyncedQuery = query(
        collection(db, 'videos'),
        orderBy('lastSynced', 'desc'),
        limit(candidateLimit)
      );
      queryTasks.push(getDocs(lastSyncedQuery));
    }

    const [createdAtSnap, maybeYoutubeSnap, maybeLastSyncedSnap] = await Promise.all(queryTasks);
    const youtubeDocs = maybeYoutubeSnap?.docs ?? [];
    const lastSyncedDocs = maybeLastSyncedSnap?.docs ?? [];

    const createdAtDocs = createdAtSnap.docs.slice(0, candidateLimit);
    const mergedDocs = new Map<string, QueryDocumentSnapshot<DocumentData>>();
    for (const docSnap of createdAtDocs) {
      mergedDocs.set(docSnap.id, docSnap);
    }
    for (const docSnap of youtubeDocs) {
      mergedDocs.set(docSnap.id, docSnap);
    }
    for (const docSnap of lastSyncedDocs) {
      mergedDocs.set(docSnap.id, docSnap);
    }

    const rawVideos = Array.from(mergedDocs.values())
      .map((docSnap) => normalizeVideoDocument(docSnap.id, docSnap.data() as Record<string, any>))
      .filter((video) => shouldIncludeDashboardVideo(video, includeNonReady))
      .sort((a, b) => {
        const byTime = getFeedSortTimestamp(b) - getFeedSortTimestamp(a);
        if (byTime !== 0) return byTime;
        return b.id.localeCompare(a.id);
      })
      .slice(0, pageSize);

    const withCreators = await enrichVideosWithCreatorInfo(rawVideos);
    const videos = await enrichYouTubeVideoMetadata(withCreators);
    const hasMore = createdAtSnap.docs.length > candidateLimit;

    return {
      videos,
      nextCursor: hasMore ? createdAtDocs[createdAtDocs.length - 1] ?? null : null,
      hasMore,
    };
  } catch (err) {
    logger.error('Failed to load dashboard feed page:', err);
    return { videos: [], nextCursor: null, hasMore: false };
  }
}

export async function listDashboardVideosAll(options?: {
  includeNonReady?: boolean;
  maxVideos?: number;
}): Promise<Video[]> {
  const includeNonReady = options?.includeNonReady === true;
  const maxVideos = Math.max(1, Math.min(options?.maxVideos ?? 2000, 10000));

  try {
    const snap = await getDocs(collection(db, 'videos'));
    const rawVideos = snap.docs
      .map((docSnap) => normalizeVideoDocument(docSnap.id, docSnap.data() as Record<string, any>))
      .filter((video) => shouldIncludeDashboardVideo(video, includeNonReady))
      .sort((a, b) => {
        const byTime = getFeedSortTimestamp(b) - getFeedSortTimestamp(a);
        if (byTime !== 0) return byTime;
        return b.id.localeCompare(a.id);
      })
      .slice(0, maxVideos);

    const withCreators = await enrichVideosWithCreatorInfo(rawVideos);
    return enrichYouTubeVideoMetadata(withCreators);
  } catch (err) {
    logger.error('Failed to load full dashboard feed:', err);
    return [];
  }
}

export async function getTrendingVideos(days = 1, max = 10): Promise<Video[]> {
  try {
    const since = new Date();
    since.setDate(since.getDate() - days);
    const qTrending = query(
      collection(db, 'videos'),
      where('lastViewed', '>=', Timestamp.fromDate(since)),
      orderBy('lastViewed', 'desc'),
      orderBy('views', 'desc'),
      limit(max)
    );
    const snap = await getDocs(qTrending);
    const videos = snap.docs.map((d) => normalizeVideoDocument(d.id, d.data() as Record<string, any>));
    const withCreators = await enrichVideosWithCreatorInfo(videos);
    return enrichYouTubeVideoMetadata(withCreators);
  } catch (err) {
    logger.error('Failed to get trending videos:', err);
    return [];
  }
}

export async function getVideoById(id: string): Promise<Video> {
  try {
    const docRef = doc(db, 'videos', id);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const video = normalizeVideoDocument(id, snap.data() as Record<string, any>);
      const enriched = await enrichVideosWithCreatorInfo([video]);
      return enriched[0];
    }
    logger.info(`Video ${id} not found in Firestore.`);
  } catch (err) {
    logger.error(`Error fetching video ${id}:`, err);
  }
  // Fallback: infer source from ID shape
  const isYouTubeId = looksLikeYouTubeVideoId(id);
  return {
    id,
    title: 'Unknown Title',
    thumbnail: isYouTubeId
      ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
      : '', // Empty for native videos - let UI handle placeholder
    duration: 0,
    type: 'video',
    source: isYouTubeId ? 'youtube' : 'native',
  };
}

export async function getContinueWatching(userId: string): Promise<Video[]> {
  try {
    const docRef = doc(db, 'userWatchHistory', userId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return [];
    return (docSnap.data().videos as Video[]) || [];
  } catch (err) {
    logger.error(`Failed to get continue watching videos for ${userId}:`, err);
    return [];
  }
}

export async function getDailyPlaylist(userId: string): Promise<Video[]> {
  try {
    const docRef = doc(db, 'userPlaylists', userId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return [];
    return (docSnap.data().daily as Video[]) || [];
  } catch (err) {
    logger.error(`Failed to get daily playlist for ${userId}:`, err);
    return [];
  }
}

export async function getLiveVideos(): Promise<Video[]> {
  try {
    const colRef = collection(db, 'videos');
    const qLive = query(colRef, where('type', '==', 'live'));
    const snap = await getDocs(qLive);
    return snap.docs.map((d) => normalizeVideoDocument(d.id, d.data() as Record<string, any>));
  } catch (err) {
    logger.error('Failed to fetch live videos:', err);
    return [];
  }
}

// Ingest a single creator's YouTube videos into Firestore
export async function ingestCreatorVideos(creatorId: string, channelId: string) {
  try {
    const uploadsPlaylistId = await getUploadsPlaylistId(channelId);
    const videos = await getAllChannelVideos(uploadsPlaylistId);

    for (const video of videos) {
      const type = video.type ?? 'video'; // type already set from isPortrait in getAllChannelVideos
      await setDoc(doc(db, 'videos', video.id), {
        ...video,
        type,
        creatorId,
        source: 'youtube', // Mark YouTube-sourced content
        lastSynced: new Date().toISOString(),
      }, { merge: true });
    }
    logger.info(`Ingested ${videos.length} videos for creator ${creatorId}`);
  } catch (error) {
    logger.error(`Error ingesting videos for creator ${creatorId}:`, error);
  }
}

// Ingest ALL creators' YouTube videos into Firestore (admin/cron only)
export async function ingestAllCreatorsVideosToFirestore() {
  try {
    const creatorsSnapshot = await getDocs(collection(db, 'creators'));
    const creators = creatorsSnapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    })) as Array<{ id: string; channelId?: string; [key: string]: unknown }>;

    for (const creator of creators) {
      if (!creator.channelId) continue;
      const uploadsPlaylistId = await getUploadsPlaylistId(creator.channelId);
      const videos = await getAllChannelVideos(uploadsPlaylistId);

      for (const video of videos) {
        const type = video.type ?? 'video'; // type already set from isPortrait in getAllChannelVideos
        const docRef = doc(db, 'videos', video.id);
        await setDoc(docRef, {
          ...video,
          type,
          creatorId: creator.id,
          source: 'youtube', // Mark YouTube-sourced content
          lastSynced: new Date().toISOString(),
        }, { merge: true });
      }
    }
    logger.info('Ingestion complete!');
  } catch (error) {
    logger.error('Error ingesting all creators videos:', error);
    throw handleAPIError(error);
  }
}

function parseNumeric(value?: number | string): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isNineBySixteen(
  aspectRatio?: string,
  width?: number | string,
  height?: number | string
): boolean {
  if (aspectRatio) {
    const normalized = aspectRatio.trim().toLowerCase();
    if (normalized === '9:16' || normalized === '9/16' || normalized === '9x16') {
      return true;
    }
  }
  const w = parseNumeric(width);
  const h = parseNumeric(height);
  if (w && h && h !== 0) {
    const ratio = w / h;
    const target = 9 / 16;
    const tolerance = 0.01;
    return Math.abs(ratio - target) <= tolerance;
  }
  return false;
}

export function isShortVideo(video: Partial<Video>): boolean {
  // Pixel dimensions are the strongest signal for native videos
  if (video.width && video.height) {
    return isNineBySixteen(video.aspectRatio, video.width, video.height);
  }
  // Explicit aspect ratio string
  if (video.aspectRatio) {
    return isNineBySixteen(video.aspectRatio);
  }
  // For YouTube videos: duration ≤ 3 min is the primary signal (YouTube Shorts cap)
  const source = video.source ?? (video.youtubeId ? 'youtube' : 'native');
  if (source === 'youtube' && typeof video.duration === 'number' && video.duration > 0) {
    return video.duration <= 180;
  }
  // Final fallback: trust stored type field
  return video.type === 'short';
}

export function getVideoRoute(video: Partial<Video> & { id?: string }): string {
  if (!video?.id) return '/shorts';
  return isShortVideo(video) ? `/shorts?v=${video.id}` : `/video/${video.id}`;
}
